import { useState } from 'react';
import { findLanguage, naarEur, type ChatMessage, type Slot, type Thumb } from '@tolk/shared';
import type { Teksten } from '../teksten';
import { alsRegels } from '../lib/regels';

/**
 * Een bubbel.
 *
 * WAT GROOT STAAT EN WAT KLEIN, en waarom dat per kant verschilt.
 *
 * Je eigen zin: de ORIGINELE tekst staat groot. Dat is wat je wilt nakijken -
 * heeft de app verstaan wat je zei? De vertaling kun je toch niet beoordelen,
 * anders had je geen tolk nodig.
 *
 * Een zin van de ander: de VERTALING staat groot, want dat is het enige dat je
 * begrijpt.
 *
 * Kort gezegd: wat in JOUW taal staat, staat groot. De andere tekst staat
 * klein en ingeklapt - hij is er wel, maar hij hoort niet in de weg te staan.
 */
export default function Bericht({
  bericht,
  mijnSlot,
  t,
  klinkt = false,
  wacht = false,
  speeltAf = false,
  grootte = 1,
  speelOpnieuw,
  stopAfspelen,
  speelBron,
  duimTranscript,
  duimVertaling,
  gegevenTranscript = null,
  gegevenVertaling = null,
  opnieuwProberen,
  bezigMetOpnieuwProberen = false,
}: {
  bericht: ChatMessage;
  mijnSlot: Slot;
  t: Teksten;
  klinkt?: boolean;
  /**
   * true zolang het geluid van DIT bericht onderweg is.
   *
   * Stond de zin nog in het geheugen van de server, dan duurt dat niets. Was
   * hij vervallen, dan spreekt de server hem opnieuw in en kost het seconden.
   * De knop moet dat verschil laten zien, anders lijkt hij stuk.
   */
  wacht?: boolean;
  /**
   * true zodra er ERGENS geluid speelt, ook bij een ander bericht.
   *
   * Alle knoppen gaan dan uit. Twee stemmen door elkaar is onverstaanbaar, en
   * met alleen `klinkt` kon je nog wel op de knoppen van andere berichten
   * drukken.
   */
  speeltAf?: boolean;
  /** 0 tot 4. Instelbaar met de plus- en minknop boven in de kamer. */
  grootte?: number;
  /** Alleen gezet bij een bericht van de ander: die heeft de audio gekregen. */
  speelOpnieuw?: (() => void) | undefined;
  /**
   * Het geluid stilzetten. Dezelfde knop, andere stand.
   *
   * GEEN TWEEDE KNOP ERNAAST, en dat volgt de handleiding hiernaast: daar is
   * ▶ ook een schakelaar die ■ wordt. Een rij met twee knoppen waarvan er
   * altijd een zinloos is, kost breedte op een telefoon en leest slechter.
   */
  stopAfspelen?: (() => void) | undefined;
  /** De opname van de spreker zelf, als die is meegestuurd. */
  speelBron?: (() => void) | undefined;
  /** Duim over het TRANSCRIPT ("is dit wat ik zei"). Alleen van jezelf, en alleen ingesproken. */
  duimTranscript?: ((thumb: Thumb) => void) | undefined;
  /** Duim over de VERTALING. Alleen van de ander. */
  duimVertaling?: ((thumb: Thumb) => void) | undefined;
  /** Welke duim al gegeven is voor dit kind - null als nog geen. */
  gegevenTranscript?: Thumb | null;
  gegevenVertaling?: Thumb | null;
  /** Duim omlaag op de vertaling: opnieuw proberen met een zwaarder model. */
  opnieuwProberen?: (() => void) | undefined;
  bezigMetOpnieuwProberen?: boolean;
}) {
  const [uitgeklapt, setUitgeklapt] = useState(false);
  const vanMij = bericht.senderSlot === mijnSlot;

  const bronTaal = findLanguage(bericht.srcLang);
  const doelTaal = findLanguage(bericht.dstLang);

  // De tekst in jouw taal is de hoofdtekst.
  const hoofd = vanMij
    ? { tekst: bericht.originalText, taal: bronTaal, label: t.jijZei }
    : { tekst: bericht.translatedText, taal: doelTaal, label: t.vertaald };
  const bij = vanMij
    ? { tekst: bericht.translatedText, taal: doelTaal, label: t.vertaald }
    : { tekst: bericht.originalText, taal: bronTaal, label: t.origineel };

  const bezig = bericht.status === 'pending';
  const mislukt = bericht.status === 'failed';

  // Vaste trappen in plaats van vrije pixels: zo blijft de regelafstand kloppen
  // en botst het nooit met de rest van het scherm.
  const TRAPPEN = ['text-base', 'text-xl', 'text-2xl', 'text-3xl', 'text-4xl'] as const;
  const maat = TRAPPEN[Math.max(0, Math.min(TRAPPEN.length - 1, grootte))] ?? 'text-xl';

  return (
    <div className={`flex ${vanMij ? 'justify-end' : 'justify-start'} animate-fade-in`}>
      <div
        className={`max-w-[92%] rounded-2xl border-2 p-3 ${
          vanMij ? 'border-gio-blue/35 bg-gio-blue/5' : 'border-gio-orange/35 bg-gio-orange/5'
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="flex items-center gap-1.5">
            <span className="text-sm leading-none">{hoofd.taal?.flag}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450">
              {hoofd.label}
            </span>
          </span>

          <span className="flex items-center gap-1.5 shrink-0">
            {speelBron !== undefined && (
              <button
                type="button"
                onClick={speelBron}
                disabled={speeltAf}
                title={t.hoorDeAnder}
                className="w-8 h-8 rounded-lg border-2 border-slate-250 bg-white text-slate-550
                           hover:border-slate-350 flex items-center justify-center
                           transition-colors duration-155 disabled:opacity-40"
              >
                <span className="text-xs leading-none">🗣</span>
              </button>
            )}
            {speelOpnieuw !== undefined && !bezig && (
              <button
                type="button"
                onClick={klinkt && stopAfspelen !== undefined ? stopAfspelen : speelOpnieuw}
                // UIT zolang er ERGENS geluid speelt — behalve bij het bericht
                // dat zelf klinkt, want dat is nu juist de knop waarmee je het
                // stopzet. Twee stemmen door elkaar blijft zo onmogelijk: de
                // andere knoppen staan nog steeds op slot.
                //
                // Tot 15 september 2026 stond ook DEZE op slot. Een lange
                // vertaling moest je dus uitzitten, en dat was Giovanni's
                // wens sinds 8 september.
                disabled={speeltAf && !klinkt}
                aria-label={t.opnieuwAfspelen}
                // Geen aparte tekst voor de stopstand: hij is als schakelaar
                // aangekondigd, net als in de handleiding, en dat scheelt een
                // sleutel die in zestien talen vertaald moet worden.
                aria-pressed={klinkt}
                aria-busy={wacht}
                className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center
                            transition-colors duration-155 ${
                              wacht
                                ? 'border-gio-orange bg-gio-orange/10 text-gio-orange-deep animate-pulse cursor-default'
                                : klinkt
                                  ? 'border-gio-orange bg-gio-orange/15 text-gio-orange-deep hover:bg-gio-orange/25'
                                  : 'border-slate-250 bg-white text-slate-550 hover:border-slate-350'
                            }`}
              >
                <span className="text-sm leading-none">{wacht ? '⋯' : klinkt ? '■' : '▶'}</span>
              </button>
            )}
            {duimTranscript !== undefined && (
              <DuimKnoppen gegeven={gegevenTranscript} opDuim={duimTranscript} t={t} />
            )}
            {duimVertaling !== undefined && (
              <DuimKnoppen gegeven={gegevenVertaling} opDuim={duimVertaling} t={t} />
            )}
          </span>
        </div>

        {/* De hoofdtekst: groot, want dit is de taal die je leest. */}
        {bezig && hoofd.tekst === null ? (
          <p className="text-base italic text-slate-450">{t.vertalen}</p>
        ) : mislukt ? (
          <p className="text-base text-gio-danger">{t.vertalenMislukt}</p>
        ) : (
          <p className={`${maat} font-bold text-gio-dark leading-snug`}>
            <Regels tekst={hoofd.tekst ?? '…'} />
          </p>
        )}

        {/* Duim omlaag op de vertaling: het bestaande zwaardere model nog een kans geven. */}
        {opnieuwProberen !== undefined && gegevenVertaling === 'down' && (
          <button
            type="button"
            onClick={opnieuwProberen}
            disabled={bezigMetOpnieuwProberen}
            className="mt-2 text-[11px] font-bold text-gio-blue hover:text-gio-dark
                       underline decoration-dotted disabled:opacity-40 disabled:no-underline"
          >
            {bezigMetOpnieuwProberen ? t.opnieuwProberenBezig : t.opnieuwProberenKnop}
          </button>
        )}

        {/* De andere taal: klein en ingeklapt. */}
        {bij.tekst !== null && bij.tekst !== '' && (
          <div className="mt-2 pt-2 border-t-2 border-slate-150">
            <button
              type="button"
              onClick={() => setUitgeklapt((n) => !n)}
              aria-expanded={uitgeklapt}
              className="flex items-center gap-1.5 text-[10px] font-bold uppercase
                         tracking-wider text-slate-450 hover:text-slate-650
                         transition-colors duration-155"
            >
              <span className="text-xs leading-none">{bij.taal?.flag}</span>
              <span>{bij.label}</span>
              <span className="font-mono">{uitgeklapt ? '−' : '+'}</span>
            </button>
            {uitgeklapt && (
              <p className="text-sm text-slate-650 leading-snug mt-1 animate-fade-in">
                <Regels tekst={bij.tekst} />
              </p>
            )}
          </div>
        )}

        {bericht.debug !== undefined && (
          <p className="mt-2 pt-1.5 border-t-2 border-slate-150 font-mono text-[10px] text-slate-450">
            {bericht.debug.translateMs} ms
            {bericht.debug.speakMs !== null && ` + ${bericht.debug.speakMs} ms spraak`} ·{' '}
            {bericht.debug.promptTokens} in / {bericht.debug.outputTokens} uit · €{' '}
            {naarEur(bericht.debug.costUsd).toFixed(5)}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Gesproken tekst, in leesregels.
 *
 * Het model levert een lap tekst zonder regelovergangen. Vier zinnen achter
 * elkaar in een bubbel lees je niet, die scan je - en dan mis je er een. Elke
 * zin krijgt hier zijn eigen regel, met een beetje lucht ertussen.
 *
 * Losse blokken in plaats van een pre-line-string: zo kun je de ruimte tussen
 * de zinnen apart zetten van de regelafstand BINNEN een lange zin.
 */
function Regels({ tekst }: { tekst: string }) {
  const regels = alsRegels(tekst);
  return (
    <>
      {regels.map((regel, i) => (
        <span key={`${i}-${regel}`} className={i === 0 ? 'block' : 'block mt-1.5'}>
          {regel}
        </span>
      ))}
    </>
  );
}

/** Twee knopjes, samen een duim. Op slot zodra er een keer geklikt is. */
function DuimKnoppen({
  gegeven,
  opDuim,
  t,
}: {
  gegeven: Thumb | null;
  opDuim: (thumb: Thumb) => void;
  t: Teksten;
}) {
  return (
    <span className="flex items-center gap-0.5 shrink-0">
      <button
        type="button"
        onClick={() => opDuim('up')}
        disabled={gegeven !== null}
        title={t.duimOmhoog}
        className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center text-xs
                    transition-colors duration-155 disabled:cursor-not-allowed ${
                      gegeven === 'up'
                        ? 'border-emerald-350 bg-emerald-50'
                        : 'border-slate-250 bg-white hover:border-slate-350 disabled:opacity-30'
                    }`}
      >
        👍
      </button>
      <button
        type="button"
        onClick={() => opDuim('down')}
        disabled={gegeven !== null}
        title={t.duimOmlaag}
        className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center text-xs
                    transition-colors duration-155 disabled:cursor-not-allowed ${
                      gegeven === 'down'
                        ? 'border-gio-danger bg-gio-danger/10'
                        : 'border-slate-250 bg-white hover:border-slate-350 disabled:opacity-30'
                    }`}
      >
        👎
      </button>
    </span>
  );
}
